import CreateButton from "../components/CreateButton"
import {
    BlockTypeSelect,
    BoldItalicUnderlineToggles,
    CreateLink,
    diffSourcePlugin,
    DiffSourceToggleWrapper,
    headingsPlugin,
    linkDialogPlugin,
    linkPlugin,
    listsPlugin,
    ListsToggle,
    MDXEditor,
    quotePlugin,
    Separator,
    tablePlugin,
    thematicBreakPlugin,
    toolbarPlugin,
    UndoRedo
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import rehypeRaw from 'rehype-raw';
import { v4 } from "uuid";
import api from '../api';
import { ReactComponent as AnnouncementIcon } from "../assets/images/icons/ic-announcement.svg";
import { ReactComponent as CloseIcon } from "../assets/images/icons/ic-close.svg";
import { ReactComponent as DeleteIcon } from "../assets/images/icons/ic-delete.svg";
import { ReactComponent as EditIcon } from "../assets/images/icons/ic-edit.svg";
import { ReactComponent as MinimizeIcon } from "../assets/images/icons/ic-minimize.svg";
import { Each } from '../common/Each';
import { ButtonStatus, CourseType } from '../common/constants';
import Button from '../components/Button';
import MeatBallsMenu from '../components/MeatBallsMenu';
import Select from '../components/Select';
import Skeleton from '../components/Skeleton';
import Spinner from '../components/Spinner';
import CommunicationsCard from '../components/cards/CommunicationsCard';
import AlertDialog from '../components/dialogs/AlertDialog';
import HeaderFooterLayout from '../components/layouts/HeaderFooterLayout';
import TextInput from '../components/TextInput';
import { ReactComponent as FilterIcon } from "../assets/images/icons/ic-filter.svg";
import { formatDateV2, formatTimeV2 } from '../utils';
import styles from './Communications.module.css';
import MaterialButton from "../components/MaterialButton"
import { Helmet, HelmetProvider } from 'react-helmet-async';

const filterSelectOptions = (options, query) => {
    const normalizedQuery = (query || '').toLocaleLowerCase('it').trim()
    if (!normalizedQuery) return options
    return options.filter(option => option.name.toLocaleLowerCase('it').includes(normalizedQuery))
}

const Communications = () => {

    const navigate = useNavigate()
    const [filtersOpen, setFiltersOpen] = useState(false)
    const [communications, setCommunications] = useState([])
    const [editCommunication, setEditCommunication] = useState(null)
    const [deleteCommunication, setDeleteCommunication] = useState(null)
    const [randomID] = useState(v4())
    const [publishState, setPublishState] = useState(null)
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [openDialog, setOpenDialog] = useState(false)
    const [composerMinimized, setComposerMinimized] = useState(false)
    const [composerDirty, setComposerDirty] = useState(false)
    const [confirmComposerClose, setConfirmComposerClose] = useState(false)
    const [courses, setCourses] = useState([])
    const [loadingCourses, setLoadingCourses] = useState(true)
    const [selectSearch, setSelectSearch] = useState({ course: '', edition: '', module: '' })
    const [filters, setFilters] = useState({
        q: '',
        courseId: null,
        editionId: null,
        moduleId: null,
        fromDate: '',
        toDate: '',
    })
    const [page, setPage] = useState(0)
    const [per] = useState(15)
    const [total, setTotal] = useState(0)
    const sentinelRef = useRef(null)
    const paginationLockRef = useRef(false)
    const requestIdRef = useRef(0)

    const getCommunications = useCallback(async () => {
        const requestId = ++requestIdRef.current
        if (page === 0) {
            setLoading(true)
        }
        else {
            setLoadingMore(true)
        }

        try {
            const params = new URLSearchParams({ page: String(page), per: String(per) })
            if (filters.q.trim()) params.set('q', filters.q.trim())
            if (filters.courseId) params.set('course_id', filters.courseId)
            if (filters.editionId) params.set('edition_id', filters.editionId)
            if (filters.moduleId) params.set('module_id', filters.moduleId)
            if (filters.fromDate) params.set('from_date', filters.fromDate)
            if (filters.toDate) params.set('to_date', filters.toDate)

            let response = await api.get(`/admin/communications?${params.toString()}`)
            if (requestId !== requestIdRef.current) {
                return
            }
            const incoming = response.communications || []
            setCommunications((prev) => {
                if (page === 0) {
                    return incoming
                }

                const existingIds = new Set(prev.map((item) => `${item.type}-${item.id}`))
                const toAppend = incoming.filter((item) => !existingIds.has(`${item.type}-${item.id}`))
                return [...prev, ...toAppend]
            })
            setTotal(response.total || 0)
        }
        catch (error) {
            console.error(error)
        }
        finally {
            if (requestId === requestIdRef.current) {
                setLoading(false)
                setLoadingMore(false)
                paginationLockRef.current = false
            }
        }
    }, [page, per, filters])

    useEffect(() => {
        const loadCourses = async () => {
            setLoadingCourses(true)
            try {
                const response = await api.get('/admin/courses?complete=true')
                setCourses((response || [])
                    .filter(course => course.type !== CourseType.Webinar && course.archived !== true)
                    .sort((a, b) => a.name.localeCompare(b.name)))
            }
            catch (error) {
                console.error(error)
            }
            finally {
                setLoadingCourses(false)
            }
        }

        loadCourses()
    }, [])

    const updateFilters = (updates) => {
        setCommunications([])
        setTotal(0)
        setPage(0)
        paginationLockRef.current = false
        setFilters((previous) => ({ ...previous, ...updates }))
    }

    const courseOptions = useMemo(
        () => courses.map(course => ({ id: course.id, name: course.name, value: course.id })),
        [courses]
    )

    const editionOptions = useMemo(() => courses
        .filter(course => !filters.courseId || course.id === filters.courseId)
        .flatMap(course => (course.editions || []).map(edition => ({
            id: edition.id,
            name: filters.courseId ? edition.name : `${course.name} • ${edition.name}`,
            value: edition.id,
            courseId: course.id,
            modules: edition.modules || [],
        }))), [courses, filters.courseId])

    const moduleOptions = useMemo(() => editionOptions
        .filter(edition => !filters.editionId || edition.id === filters.editionId)
        .flatMap(edition => (edition.modules || []).map(module => ({
            id: module.id,
            name: filters.editionId ? module.name : `${edition.name} • ${module.name}`,
            value: module.id,
            courseId: edition.courseId,
            editionId: edition.id,
        }))), [editionOptions, filters.editionId])

    const selectedCourse = courseOptions.find(option => option.id === filters.courseId) || null
    const selectedEdition = editionOptions.find(option => option.id === filters.editionId) || null
    const selectedModule = moduleOptions.find(option => option.id === filters.moduleId) || null
    const filteredCourseOptions = useMemo(
        () => filterSelectOptions(courseOptions, selectSearch.course),
        [courseOptions, selectSearch.course]
    )
    const filteredEditionOptions = useMemo(
        () => filterSelectOptions(editionOptions, selectSearch.edition),
        [editionOptions, selectSearch.edition]
    )
    const filteredModuleOptions = useMemo(
        () => filterSelectOptions(moduleOptions, selectSearch.module),
        [moduleOptions, selectSearch.module]
    )

    const handleCourseFilter = (option) => {
        setSelectSearch(previous => ({ ...previous, edition: '', module: '' }))
        updateFilters({
            courseId: option?.value ?? null,
            editionId: null,
            moduleId: null,
        })
    }

    const handleEditionFilter = (option) => {
        setSelectSearch(previous => ({ ...previous, module: '' }))
        updateFilters({
            courseId: option?.courseId ?? filters.courseId,
            editionId: option?.value ?? null,
            moduleId: null,
        })
    }

    const handleModuleFilter = (option) => {
        updateFilters({
            courseId: option?.courseId ?? filters.courseId,
            editionId: option?.editionId ?? filters.editionId,
            moduleId: option?.value ?? null,
        })
    }

    const closeComposer = () => {
        setOpenDialog(false)
        setComposerMinimized(false)
        setComposerDirty(false)
        setConfirmComposerClose(false)
    }

    const requestComposerClose = () => {
        if (composerDirty) {
            setConfirmComposerClose(true)
            return
        }
        closeComposer()
    }

    useEffect(() => {
        getCommunications()
    }, [getCommunications])

    useEffect(() => {
        const sentinel = sentinelRef.current
        if (!sentinel) {
            return
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const firstEntry = entries[0]
                if (!firstEntry?.isIntersecting) {
                    return
                }

                const canLoadMore = communications.length < total
                if (loading || loadingMore || !canLoadMore || paginationLockRef.current) {
                    return
                }

                paginationLockRef.current = true
                setPage((prev) => prev + 1)
            },
            { root: null, rootMargin: '200px', threshold: 0 }
        )

        observer.observe(sentinel)
        return () => observer.disconnect()
    }, [communications.length, total, loading, loadingMore])

    const updateCommunication = async () => {
        setPublishState(ButtonStatus.Loading)
        try {
            await api.put(`/admin/communications/${editCommunication.id}/${editCommunication.type}`, { message: editCommunication.message, medias: editCommunication.medias ?? [] })
            setCommunications((prev) => {
                return prev.map((communication) => {
                    if (communication.id !== editCommunication.id || communication.type !== editCommunication.type) {
                        return communication
                    }
                    return {
                        ...communication,
                        message: editCommunication.message,
                        medias: editCommunication.medias ?? [],
                    }
                })
            })
            setPublishState(ButtonStatus.Success)
            setEditCommunication(null)
            getCommunications()
        }
        catch (e) {
            setPublishState(ButtonStatus.Error)
            console.error(e)
        }
    }

    const removeFile = async (url) => {
        setEditCommunication((prev) => {
            return { ...prev, medias: (prev.medias || []).filter(media => media.url !== url) }
        })
    }

    return (
        <HeaderFooterLayout>
            <HelmetProvider>
                <Helmet>
                    <title>Didattica - Annunci</title>
                </Helmet>
            </HelmetProvider>
            <div className={styles.container}>
                <div className={styles.section}>
                    <div className={styles.sectionInner}>

                        <div className={styles.header}>
                            <div>
                                <p className={styles.eyebrow}>Didattica</p><h1 className={styles.pageTitle}>Annunci</h1>
                                <div className={styles.headerDescription}>Cerca, filtra e gestisci le comunicazioni inviate agli studenti.</div>
                            </div>
                            <CreateButton style={{ padding: ".75rem 1rem" }} onClick={() => {
                                    setOpenDialog(true)
                                    setComposerMinimized(false)
                                    setComposerDirty(false)
                                }}>
                                Nuovo annuncio

                            </CreateButton>
                        </div>
                        <div className={styles.filterCard}>
                        <div className={styles.searchToolbar}>
                            <div className={styles.searchFilter}>
                                <TextInput appearance="modern"
                                    type="search"
                                    value={filters.q}
                                    placeholder="Cerca negli annunci..."
                                    onKeyUp={(value) => updateFilters({ q: value })}
                                />
                            </div>
                            <button type="button" className={styles.filterButton} aria-expanded={filtersOpen} aria-controls="announcement-filters" onClick={() => setFiltersOpen(value => !value)}><FilterIcon /> Filtri</button>
                        </div>
                        <div className={styles.filters} id="announcement-filters" hidden={!filtersOpen}>
                            <Select appearance="modern" portal
                                loading={loadingCourses}
                                disabled={loadingCourses}
                                options={filteredCourseOptions}
                                selected={selectedCourse}
                                placeholder="Tutti i corsi"
                                nullable
                                nullLabel="Tutti i corsi"
                                onSearch={(value) => setSelectSearch(previous => ({ ...previous, course: value || '' }))}
                                searchPlaceholder="Cerca corso"
                                onSelect={handleCourseFilter}
                            />
                            <Select appearance="modern" portal
                                loading={loadingCourses}
                                disabled={loadingCourses || editionOptions.length === 0}
                                options={filteredEditionOptions}
                                selected={selectedEdition}
                                placeholder="Tutte le edizioni"
                                nullable
                                nullLabel="Tutte le edizioni"
                                onSearch={(value) => setSelectSearch(previous => ({ ...previous, edition: value || '' }))}
                                searchPlaceholder="Cerca edizione"
                                onSelect={handleEditionFilter}
                            />
                            <Select appearance="modern" portal
                                loading={loadingCourses}
                                disabled={loadingCourses || moduleOptions.length === 0}
                                options={filteredModuleOptions}
                                selected={selectedModule}
                                placeholder="Tutti i moduli"
                                nullable
                                nullLabel="Tutti i moduli"
                                onSearch={(value) => setSelectSearch(previous => ({ ...previous, module: value || '' }))}
                                searchPlaceholder="Cerca modulo"
                                onSelect={handleModuleFilter}
                            />
                            <div className={styles.dateRangeFilter}>
                                <span className={styles.dateRangeLabel}>Dal</span>
                                <input
                                    type="date"
                                    value={filters.fromDate}
                                    max={filters.toDate || undefined}
                                    aria-label="Data iniziale"
                                    title="Data iniziale"
                                    onChange={(event) => {
                                        const fromDate = event.target.value
                                        updateFilters({
                                            fromDate,
                                            toDate: filters.toDate && fromDate > filters.toDate ? fromDate : filters.toDate,
                                        })
                                    }}
                                />
                                <span className={styles.dateRangeLabel}>al</span>
                                <input
                                    type="date"
                                    value={filters.toDate}
                                    min={filters.fromDate || undefined}
                                    aria-label="Data finale"
                                    title="Data finale"
                                    onChange={(event) => updateFilters({ toDate: event.target.value })}
                                />
                                {(filters.fromDate || filters.toDate) &&
                                    <button
                                        type="button"
                                        className={styles.clearDateRange}
                                        aria-label="Rimuovi filtro date"
                                        title="Rimuovi filtro date"
                                        onClick={() => updateFilters({ fromDate: '', toDate: '' })}
                                    >
                                        <CloseIcon />
                                    </button>
                                }
                            </div>
                        </div>
                        </div>
                        {!loading &&
                            <div className={styles.listHeading}><h2>Elenco annunci</h2><div className={styles.resultCount}>
                                {total === 1 ? '1 annuncio' : `${total} annunci`}
                            </div></div>
                        }
                        {loading &&
                            <div className={styles.list}>
                                <Skeleton height={220} width={"100%"} borderRadius={16} />
                                <Skeleton height={220} width={"100%"} borderRadius={16} />
                                <Skeleton height={220} width={"100%"} borderRadius={16} />
                            </div>
                        }
                        {!loading && communications.length === 0 &&
                            <div className={styles.emptyState}>
                                <AnnouncementIcon />
                                <div className={styles.emptyTitle}>Nessun annuncio trovato</div>
                                <div>Prova a modificare la ricerca o i filtri selezionati.</div>
                            </div>
                        }
                        {!loading && communications.length > 0 &&
                            <div className={styles.list}>
                                <Each of={communications} render={(communication) => {
                                    const isEditing = editCommunication?.id === communication.id && editCommunication.type === communication.type
                                    const destination = communication.module
                                        ? `/courses/${communication.course.id}/editions/${communication.edition.id}/modules/${communication.module.id}`
                                        : `/courses/${communication.course.id}/editions/${communication.edition.id}`

                                    return (
                                <article className={`${styles.announcementCard} ${isEditing ? styles.editingCard : ""}`}>
                                    <div className={styles.cardHeader}>
                                        <div className={styles.cardHeading}>
                                            <div className={styles.metadata}>
                                                <button type="button" className={styles.courseLink} onClick={() => navigate(destination)}>
                                                    {communication.course.name}
                                                </button>
                                                <span className={styles.chip}>{communication.edition.name}</span>
                                                {communication.module && <span className={`${styles.chip} ${styles.moduleChip}`}>{communication.module.name}</span>}
                                            </div>
                                            <div className={styles.title}>
                                                {communication.module?.name || communication.edition.name}
                                            </div>
                                        </div>
                                    <MeatBallsMenu
                                        disabled={isEditing}
                                        style={{ flexShrink: 0 }}
                                        actions={[
                                            {
                                                label: "Modifica", icon: EditIcon, style: { color: "var(--sf-light-green)" }, onClick: () => {
                                                    setPublishState(null)
                                                    setEditCommunication({ ...communication, medias: [...(communication.medias || [])] })
                                                }
                                            },
                                            {
                                                label: "Elimina", icon: DeleteIcon, style: { color: "var(--sf-red)" }, onClick: () => {
                                                    setDeleteCommunication(communication)
                                                }
                                            }
                                        ]} />
                                    </div>
                                    {isEditing && <div className={styles.editHeading}><span>Modifica messaggio</span><span>Le modifiche saranno applicate al salvataggio.</span></div>}
                                    <div className={`${styles.message} ${isEditing ? styles.editing : ''}`}>
                                        {!isEditing &&
                                            <Markdown rehypePlugins={[rehypeRaw]}>
                                                {communication.message}
                                            </Markdown>
                                        }
                                        {isEditing &&
                                            <MDXEditor
                                                key={randomID}
                                                className={styles.editor}
                                                contentEditableClassName={styles.editorContent}
                                                readOnly={publishState === ButtonStatus.Loading}
                                                markdown={editCommunication.message}
                                                onChange={(value) => {
                                                    setEditCommunication((prev) => {
                                                        return { ...prev, message: value }
                                                    })
                                                }}
                                                placeholder={"Scrivi qui..."}
                                                plugins={[
                                                    headingsPlugin(),
                                                    linkPlugin(),
                                                    listsPlugin(),
                                                    quotePlugin(),
                                                    thematicBreakPlugin(),
                                                    linkDialogPlugin(),
                                                    diffSourcePlugin(),
                                                    tablePlugin(),
                                                    toolbarPlugin({
                                                        toolbarContents: () => (
                                                            <>
                                                                {' '}
                                                                <UndoRedo />
                                                                <Separator />
                                                                <BoldItalicUnderlineToggles />
                                                                <Separator />
                                                                <ListsToggle />
                                                                <CreateLink />
                                                                <Separator />
                                                                <BlockTypeSelect />
                                                                <DiffSourceToggleWrapper options={["rich-text", "source"]} />
                                                            </>
                                                        )
                                                    })
                                                ]}
                                            />
                                        }
                                    </div>
                                    {(isEditing ? editCommunication.medias : communication.medias)?.length > 0 && <div className={styles.attachments}>
                                        <Each of={isEditing ? editCommunication.medias : communication.medias} render={(media) => {
                                            return (
                                                <MaterialButton modern thumbnail
                                                    material={media}
                                                    downloadable={!isEditing}
                                                    onClick={async () => {
                                                        if (publishState !== ButtonStatus.Loading) await removeFile(media.url)
                                                    }} />
                                            )
                                        }} />
                                    </div>}
                                    {isEditing && publishState === ButtonStatus.Error && <div className={styles.editError} role="alert">Impossibile salvare le modifiche. Riprova.</div>}
                                    {isEditing &&
                                        <div className={styles.actions}>
                                            <Button appearance="subtle" disabled={publishState === ButtonStatus.Loading} onClick={() => {
                                                setEditCommunication(null)
                                            }}>Annulla</Button>
                                            <Button onClick={updateCommunication} status={publishState} disabled={editCommunication.message.trim() === '' || publishState === ButtonStatus.Loading}>Salva modifiche</Button>
                                        </div>
                                    }
                                    <div className={styles.date}>
                                        {formatDateV2(communication.created_at, { day: 'numeric', month: 'long', year: 'numeric' })} - {formatTimeV2(communication.created_at)}
                                    </div>
                                </article>
                                    )
                                }} />
                            </div>
                        }
                        <div ref={sentinelRef} style={{ height: '1px', width: '100%' }} />
                        {loadingMore &&
                            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '1rem 0' }}>
                                <Spinner color={'black'} />
                            </div>
                        }
                    </div>
                </div>
                {openDialog &&
                    <div className={`${styles.composerLayer} ${composerMinimized ? styles.minimized : ''}`}>
                        <div className={styles.composerBackdrop} />
                        <section className={styles.composer} role="dialog" aria-modal={!composerMinimized} aria-label="Nuovo annuncio">
                            <div className={styles.composerHeader}>
                                <div>

                                    <div className={styles.composerTitle}>Nuovo annuncio</div>
                                </div>
                                <div className={styles.composerControls}>
                                    <button type="button" title="Riduci a icona" aria-label="Riduci a icona" onClick={() => setComposerMinimized(true)}>
                                        <MinimizeIcon />
                                    </button>
                                    <button type="button" title="Chiudi" aria-label="Chiudi" onClick={requestComposerClose}>
                                        <CloseIcon />
                                    </button>
                                </div>
                            </div>
                            <div className={styles.composerBody}>
                                <CommunicationsCard
                                    onDirtyChange={setComposerDirty}
                                    onPublish={async () => {
                                        closeComposer()
                                        updateFilters({})
                                    }}
                                />
                            </div>
                        </section>
                        <button
                            type="button"
                            className={styles.minimizedComposer}
                            aria-label="Riapri il nuovo annuncio"
                            title="Riapri il nuovo annuncio"
                            onClick={() => setComposerMinimized(false)}
                        >
                            <AnnouncementIcon />
                            <span>Nuovo annuncio</span>
                        </button>
                    </div>
                }
                <AlertDialog appearance="modern"
                    open={confirmComposerClose}
                    dialogProps={{ containerStyle: { zIndex: 1100 } }}
                    title="Chiudere il nuovo annuncio?"
                    text="Il testo e gli allegati inseriti andranno persi."
                    onClose={() => setConfirmComposerClose(false)}
                    actions={[
                        {
                            label: "Continua a modificare",
                            accentColor: "var(--sf-red)",
                            onClick: () => setConfirmComposerClose(false),
                        },
                        {
                            label: "Chiudi",
                            onClick: closeComposer,
                        },
                    ]}
                />
                <AlertDialog appearance="modern"
                    open={deleteCommunication}
                    title="Eliminare l'annuncio?"
                    text='Questa operazione non può essere annullata.'
                    onClose={() => {
                        setDeleteCommunication(null)
                    }}
                    actions={[
                        {
                            label: "ANNULLA",
                            accentColor: "var(--sf-red)",
                            onClick: () => {
                                setDeleteCommunication(null)
                            }
                        },
                        {
                            label: "ELIMINA",
                            onClick: async () => {
                                try {
                                    await api.delete(`/admin/communications/${deleteCommunication.id}/${deleteCommunication.type}`)
                                    setCommunications((prev) => {
                                        return prev.filter((communication) => !(
                                            communication.id === deleteCommunication.id
                                            && communication.type === deleteCommunication.type
                                        ))
                                    })
                                    setTotal((previous) => Math.max(0, previous - 1))
                                    setDeleteCommunication(null)
                                }
                                catch (e) {
                                    console.error(e)
                                }
                            }
                        }
                    ]} />
            </div>
        </HeaderFooterLayout>
    )
}

export default Communications;
